import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { notify } from 'reducers/NotifierReducer';
import { executeApiAction } from '../../shared/utility/apiHelper';
import { searchEvidenceRecords } from '../services/evidenceService';
import { getClearanceLevelInt } from '../utility/evidenceClearanceHelper';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../constants/evidenceRecordsFilters';

const DEFAULT_GRID_STATE = {
  searchText: '',
  page: DEFAULT_PAGE,
  pageSize: DEFAULT_PAGE_SIZE,
  sortModel: [],
};

const usePortalMyEvidenceRecords = (personId) => {
  const dispatch = useDispatch();
  const agencyId = useSelector((state) => state.user?.userAgency);
  const userCurrentClearanceLevel = useSelector((state) => state.user?.userCurrentClearanceLevel);
  const clearanceLevel = useMemo(() => getClearanceLevelInt(userCurrentClearanceLevel), [
    userCurrentClearanceLevel,
  ]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  // { status: [ids] } | { overdue: true } | null — a single active KPI-tile filter, like the
  // reference portal's one-active-stat-card-at-a-time behavior.
  const [tileFilter, setTileFilter] = useState(null);
  const gridStateRef = useRef(DEFAULT_GRID_STATE);

  const runSearch = useCallback(
    (gridState, tileFilterOverride) => {
      if (!agencyId || !personId) return;

      const nextTileFilter = tileFilterOverride !== undefined ? tileFilterOverride : tileFilter;

      executeApiAction({
        setLoading,
        dispatch,
        action: () =>
          searchEvidenceRecords({
            filters: {
              // Matches the officer as submitter, collector, OR receiver — same "my evidence"
              // scope as the reference Evidence Management portal, and consistent with
              // getMyDashboardSummary's broadened scope (see rms-evidence-records.class.js).
              involvedBy: [personId],
              searchText: gridState.searchText || null,
              status: nextTileFilter?.status || null,
              overdue: nextTileFilter?.overdue || null,
              ClearanceLevel: clearanceLevel,
            },
            agencyId,
            page: gridState.page,
            pageSize: gridState.pageSize,
            sortField: gridState.sortModel?.[0]?.field,
            sortDirection: gridState.sortModel?.[0]?.sort,
          }),
        onSuccess: (response) => {
          setItems(response.items || []);
          setTotal(response.total || 0);
        },
        onError: (error) => {
          dispatch(notify(error.message, 'error'));
        },
      });
    },
    [agencyId, personId, tileFilter, clearanceLevel, dispatch]
  );

  const handleGridStateChange = useCallback(
    (nextGridState) => {
      gridStateRef.current = nextGridState;
      runSearch(nextGridState);
    },
    [runSearch]
  );

  const applyTileFilter = useCallback(
    (nextTileFilter) => {
      setTileFilter(nextTileFilter);
      const nextGridState = { ...gridStateRef.current, page: 0 };
      gridStateRef.current = nextGridState;
      runSearch(nextGridState, nextTileFilter);
    },
    [runSearch]
  );

  const refetch = useCallback(() => runSearch(gridStateRef.current), [runSearch]);

  return {
    items,
    total,
    loading,
    tileFilter,
    handleGridStateChange,
    applyTileFilter,
    refetch,
  };
};

export default usePortalMyEvidenceRecords;
